/**
 * @description
 * React Query hook for patient care pathway data.
 * Fetches pathways from the backend via the care-pathway service,
 * falling back to mock data when the backend is unreachable.
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from './use-auth';
import { getCarePathways } from '../services/care-pathway-service';
import type { CarePathway } from '../types/care-pathway-types';

// Stable query key factory
export const carePathwayKeys = {
  all: ['carePathways'] as const,
  list: () => [...carePathwayKeys.all, 'list'] as const,
  listByPatient: (patientId: string) => [...carePathwayKeys.list(), patientId] as const,
};

/**
 * Custom hook to fetch care pathways for the currently authenticated patient.
 *
 * @returns React Query result extended with:
 *   - `pathways`           — fetched care pathways (defaults to [])
 *   - `activeCount`        — number of pathways with status === 'active'
 */
export const useCarePathways = () => {
  const { user } = useAuth();
  const userId = user?.id;

  const queryResult = useQuery<CarePathway[], Error>({
    queryKey: userId ? carePathwayKeys.listByPatient(userId) : carePathwayKeys.list(),
    queryFn: async () => {
      if (!userId) return [];
      return getCarePathways(userId);
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('4')) return false;
      return failureCount < 2;
    },
  });

  return {
    ...queryResult,
    pathways: queryResult.data ?? [],
    activeCount: queryResult.data?.filter((p) => p.status === 'active').length ?? 0,
  };
};
